/* @flow */
import * as React from "react";
import ReactDOM from "react-dom";
import throttle from "lodash/throttle";
import { HANDLE_SIZE } from "./Handle";

export const THROTTLE_DELAY = 50;

/* eslint-disable react/no-unused-prop-types */
// Somehow this type is getting inside no-unused-prop-types. Hmmm...
type Position = { x: number, y: number };
/* eslint-enable react/no-unused-prop-types */

function getPositionFromEvent(
  ev:
    | MouseEvent
    | SyntheticMouseEvent<HTMLElement>
    | TouchEvent
    | SyntheticTouchEvent<HTMLElement>
): Position {
  if (ev instanceof TouchEvent || ev.nativeEvent instanceof TouchEvent) {
    // MouseEvent is not exact, so Flow can't guarantee it doesn't have a
    // nativeEvent property which is a TouchEvent.
    let touchEvent = ((ev: any): TouchEvent);
    let position = touchEvent.touches[0];
    return { x: position.clientX, y: position.clientY };
  } else {
    let mouseEvent = ((ev: any): MouseEvent);
    return { x: mouseEvent.clientX, y: mouseEvent.clientY };
  }
}

const eventFor = {
  touch: {
    start: "touchstart",
    move: "touchmove",
    stop: "touchend",
  },
  mouse: {
    start: "mousedown",
    move: "mousemove",
    stop: "mouseup",
  },
};
let dragEventFor = eventFor.mouse;

type Props = {
  children: ({
    onMouseDown: (MouseEvent) => void,
    onTouchStartCapture: (TouchEvent) => void,
    captureRef: (?HTMLElement) => void,
  }) => React.Node,
  ratio: number,
  onStart: (number) => void,
  onDrag: (number) => void,
  onEnd: (number) => void,
  onAbort: (void) => void,
};

export default class SliderController extends React.Component<Props> {
  _slider: ?HTMLElement;

  _isMobilePlatform: boolean = false;
  _isSliding: boolean = false;

  _initialX: ?number = null;
  _initialY: ?number = null;
  _currentX: ?number = null;
  _currentY: ?number = null;
  _farthestX: number = 0;
  _farthestY: number = 0;
  _offsetX: number = 0;

  componentWillUnmount() {
    this._removeListeners();
  }

  _abort = () => {
    this._isSliding = false;
    this._removeListeners();
    ReactDOM.unstable_batchedUpdates(() => {
      this.props.onAbort();
    });
  };

  _addListeners = () => {
    window.addEventListener(dragEventFor.move, this._handleDrag);
    window.addEventListener(dragEventFor.stop, this._handleDragEnd);
  };

  _removeListeners = () => {
    window.removeEventListener(dragEventFor.move, this._handleDrag);
    window.removeEventListener(dragEventFor.stop, this._handleDragEnd);
  };

  _cleanUpMissingSlider = (): Error => {
    dragEventFor = eventFor.mouse;
    this._removeListeners();
    dragEventFor = eventFor.touch;
    this._removeListeners();
    return new Error(
      "Attempted to get the slider ratio for a slider which was not mounted. Presumably a set of listeners was not properly removed. Removing all possible listeners."
    );
  };

  _getRatio = (x: number): number => {
    if (!this._slider) {
      // Weird pattern, but flow doesn't know that this always throws unless I do it out here
      throw this._cleanUpMissingSlider();
    }
    const { left, width } = this._slider.getBoundingClientRect();
    const ratio = (x - this._offsetX - left) / width;
    return Math.max(0, Math.min(1, ratio));
  };

  _getStartingRatio = (x: number): number => {
    if (!this._slider) {
      // Weird pattern, but flow doesn't know that this always throws unless I do it out here
      throw this._cleanUpMissingSlider();
    }
    const { left, width } = this._slider.getBoundingClientRect();
    const { ratio } = this.props;
    const positionOfCurrentValue = ratio * width + left;
    if (
      x >= positionOfCurrentValue - HANDLE_SIZE / 2 &&
      x <= positionOfCurrentValue + HANDLE_SIZE / 2
    ) {
      // User started inside the old handle. Treat it like starting in the center.
      this._offsetX = x - positionOfCurrentValue;
      return ratio;
    } else {
      this._offsetX = 0;
      return this._getRatio(x);
    }
  };

  _handleMouseDown = (ev: MouseEvent) => {
    if (this._isMobilePlatform) {
      // Ignore mouse events after receiving a touchstart, because mobile devices
      // will have different behavior for how/when they send the mouse versions.
      return;
    }
    if (dragEventFor === eventFor.touch) {
      this._removeListeners();
      dragEventFor = eventFor.mouse;
    }
    this._start(ev);
  };

  _handleTouchStart = (ev: TouchEvent) => {
    if (dragEventFor === eventFor.mouse) {
      this._removeListeners();
      dragEventFor = eventFor.touch;
    }
    this._isMobilePlatform = true;
    this._start(ev);
  };

  _start = (ev: MouseEvent | TouchEvent) => {
    const position = getPositionFromEvent(ev);

    this._initialX = position.x;
    this._initialY = position.y;
    this._farthestX = 0;
    this._farthestY = 0;
    this._updateCurrentPosition(position);
    this._removeListeners();
    this._addListeners();

    this._isSliding = true;
    const ratio = this._getStartingRatio(position.x);
    this.props.onStart(ratio);
  };

  _handleDrag = throttle((ev: MouseEvent | TouchEvent) => {
    const position = getPositionFromEvent(ev);
    this._updateCurrentPosition(position);
    if (this._isProbablyScrolling()) {
      this._abort();
      return;
    }
    if (this._isSliding) {
      const ratio = this._getRatio(position.x);
      ReactDOM.unstable_batchedUpdates(() => {
        this.props.onDrag(ratio);
      });
    }
  }, THROTTLE_DELAY);

  _handleDragEnd = () => {
    this._isSliding = false;
    this._removeListeners();
    if (this._currentX == null) {
      this._abort();
      return;
    }
    const ratio = this._getRatio(this._currentX);
    ReactDOM.unstable_batchedUpdates(() => {
      this.props.onEnd(ratio);
    });
  };

  _isProbablyScrolling = (): boolean => {
    return this._farthestY > this._farthestX + 20;
  };

  _updateCurrentPosition = ({ x, y }: Position) => {
    this._currentX = x;
    this._currentY = y;
    const initialX = this._initialX;
    const initialY = this._initialY;
    if (initialX == null || initialY == null) {
      return;
    }
    if (Math.abs(x - initialX) > this._farthestX) {
      this._farthestX = Math.abs(x - initialX);
    }
    if (Math.abs(y - initialY) > this._farthestY) {
      this._farthestY = Math.abs(y - initialY);
    }
  };

  render() {
    const { children } = this.props;

    return children({
      onMouseDown: this._handleMouseDown,
      onTouchStartCapture: this._handleTouchStart,
      captureRef: (el) => {
        this._slider = el;
      },
    });
  }
}



// WEBPACK FOOTER //
// ../nx-components/src/Slider/SliderController.jsx